/**
 * Security incidents: a personal data breach, recorded and told to the clients
 * it affects (`/security-incidents`, `docs/41_LEGAL_COMPLIANCE.md`).
 *
 * For its clients' customers Connect is the processor; each client must tell
 * the Data Protection Board and the people affected, and can only do so once
 * Connect has told it. So the record asks exactly what the client needs to pass
 * on, telling clients is its own permission (`publish`), and every client's
 * notice is listed with what became of it. Connect's own report to CERT-In
 * (six hours) is recorded here too.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Send, ShieldAlert, X } from 'lucide-react';
import {
  Alert,
  api,
  ApiError,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Confirm,
  EmptyState,
  Input,
  PageHeader,
  QueryPending,
  RecordSelect,
  Sections,
  Switch,
  Textarea,
  formatDateTime,
  toast,
  useCan,
  type Tone,
} from '@connect/web-shared';

type Status = 'open' | 'notified' | 'closed';

interface IncidentRow {
  public_id: string;
  code: string;
  title: string;
  status: Status;
  detected_at: string;
  first_notified_at: string | null;
  cert_in_reported_at: string | null;
  all_clients: boolean;
  client_count: number;
  closed_at: string | null;
  /** Connect's report to CERT-In is due by then (six hours from awareness). */
  cert_in_due_at: string;
  /** Connect's own target for telling the affected clients. */
  clients_due_at: string;
  /** The clocks that passed with their step not done. */
  overdue: ('cert_in' | 'clients')[];
}

interface IncidentDetail extends IncidentRow {
  description: string;
  data_affected: string;
  consequences: string;
  measures: string;
  client_steps: string;
  contact: string;
  occurred_at: string | null;
  cert_in_reference: string | null;
  /** Where the report goes: CERT-In's own channels, from the server's settings. */
  cert_in_contact: { email: string; phone: string; fax: string };
  clients: {
    public_id: string;
    name: string;
    notice: { status: 'pending' | 'sent' | 'failed'; sent_at: string | null; attempts: number; last_error: string | null } | null;
  }[];
}

interface IncidentBody {
  title?: string;
  description?: string;
  data_affected?: string;
  consequences?: string;
  measures?: string;
  client_steps?: string;
  contact?: string;
  occurred_at?: string | null;
  detected_at?: string;
  cert_in_reported_at?: string | null;
  cert_in_reference?: string | null;
  all_clients?: boolean;
  clients?: string[];
}

const BASE = '/security-incidents';
const keys = {
  list: ['security-incidents'] as const,
  one: (id: string) => ['security-incidents', id] as const,
};
const STATUS: Record<Status, { label: string; tone: Tone }> = {
  open: { label: 'Not told yet', tone: 'danger' },
  notified: { label: 'Clients told', tone: 'warning' },
  closed: { label: 'Closed', tone: 'neutral' },
};
const CRUMBS = [{ label: 'Legal' }, { label: 'Security incidents', to: '/legal/incidents' }];
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : undefined);

/** A `datetime-local` value as an ISO instant, in the reader's own time zone. */
const toInstant = (local: string) => (local ? new Date(local).toISOString() : null);
const toLocal = (iso: string | null) => {
  if (!iso) return '';
  const at = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
};

export function SecurityIncidentsPage() {
  const navigate = useNavigate();
  const canCreate = useCan('core.security_incident.create');
  const list = useQuery({ queryKey: keys.list, queryFn: () => api.get<IncidentRow[]>(BASE) });
  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title="Security incidents"
        description="A personal data breach, and the notice each affected client was sent. A client must tell the Data Protection Board and the people affected without delay — it can only once we have told it."
        breadcrumbs={[{ label: 'Legal' }, { label: 'Security incidents' }]}
        actions={
          canCreate ? (
            <Button onClick={() => navigate('/legal/incidents/new')}>
              <Plus className="size-4" />
              Record an incident
            </Button>
          ) : null
        }
      />
      {list.data === undefined ? (
        <QueryPending query={list} label="Loading incidents" />
      ) : list.data.length === 0 ? (
        <EmptyState icon={<ShieldAlert className="size-6" />} title="No incidents" description="None has been recorded." />
      ) : (
        <Card>
          <ul className="divide-y divide-line text-sm">
            {list.data.map((row) => (
              <li key={row.public_id}>
                <Link to={`/legal/incidents/${row.public_id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-hover">
                  <span className="font-mono text-xs text-muted">{row.code}</span>
                  <span className="min-w-0 flex-1 font-medium text-heading">{row.title}</span>
                  <span className="text-xs text-muted">Aware {formatDateTime(row.detected_at)}</span>
                  {row.overdue.length ? <Badge tone="danger">Overdue</Badge> : null}
                  <Badge tone={STATUS[row.status].tone}>{STATUS[row.status].label}</Badge>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

export function SecurityIncidentPage() {
  const { publicId = '' } = useParams();
  const isNew = publicId === 'new';
  const incident = useQuery({
    queryKey: keys.one(publicId),
    queryFn: () => api.get<IncidentDetail>(`${BASE}/${publicId}`),
    enabled: !isNew,
  });
  if (isNew) return <IncidentView incident={null} />;
  if (incident.data === undefined) {
    return (
      <div className="flex flex-1 flex-col gap-4">
        <PageHeader title="Incident" breadcrumbs={CRUMBS} />
        <QueryPending query={incident} label="Loading the incident" />
      </div>
    );
  }
  return <IncidentView key={incident.data.public_id} incident={incident.data} />;
}

/**
 * The two clocks from when Connect became aware: CERT-In's six hours, and
 * Connect's own target for telling the affected clients. Every owner is
 * emailed once when one passes unmet.
 */
function Clocks({ incident }: { incident: IncidentDetail }) {
  const late = (clock: 'cert_in' | 'clients') => incident.overdue.includes(clock);
  const certIn = incident.cert_in_reported_at
    ? `Reported ${formatDateTime(incident.cert_in_reported_at)}.`
    : `Due by ${formatDateTime(incident.cert_in_due_at)}.`;
  // Where to report, said beside the clock: nobody should look it up in the six hours.
  const { email, phone, fax } = incident.cert_in_contact;
  const clients =
    incident.status === 'open'
      ? `Due by ${formatDateTime(incident.clients_due_at)}.`
      : `Told from ${formatDateTime(incident.first_notified_at ?? incident.detected_at)}.`;
  return (
    <Alert
      tone={incident.overdue.length ? 'danger' : incident.status === 'open' ? 'warning' : 'info'}
      title={incident.overdue.length ? 'Past its deadline' : 'The clock is running'}
    >
      <span className={late('cert_in') ? 'font-medium' : undefined}>
        CERT-In: {certIn}
        {incident.cert_in_reported_at ? null : (
          <>
            {' '}
            Report it to <a href={`mailto:${email}`}>{email}</a>, on {phone}, or by fax on {fax}.
          </>
        )}
      </span>{' '}
      <span className={late('clients') ? 'font-medium' : undefined}>
        Affected clients: {clients} Each must tell the Data Protection Board and the people affected, and cannot until we
        tell it.
      </span>
    </Alert>
  );
}

const FIELDS: { key: keyof IncidentBody & keyof IncidentDetail; label: string; hint: string; rows: number }[] = [
  { key: 'description', label: 'What happened', hint: 'Its nature and extent, in plain words a client can pass on.', rows: 4 },
  { key: 'data_affected', label: 'The data concerned', hint: 'Which personal data, of whom.', rows: 3 },
  { key: 'consequences', label: 'What it may mean', hint: 'The consequences likely for the people affected.', rows: 3 },
  { key: 'measures', label: 'What we have done, and are doing', hint: 'To stop it, limit it and prevent it happening again.', rows: 4 },
  { key: 'client_steps', label: 'What clients and the people affected can do', hint: 'Safety steps worth taking.', rows: 3 },
];

function IncidentView({ incident }: { incident: IncidentDetail | null }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canEdit = useCan(incident ? 'core.security_incident.edit' : 'core.security_incident.create');
  const canPublish = useCan('core.security_incident.publish');
  const closed = incident?.status === 'closed';
  const [form, setForm] = useState<Required<Pick<IncidentBody, 'title' | 'description' | 'data_affected' | 'consequences' | 'measures' | 'client_steps' | 'contact' | 'all_clients'>> & {
    occurred: string;
    detected: string;
    certIn: string;
    certInReference: string;
    clients: { public_id: string; name: string }[];
  }>({
    title: incident?.title ?? '',
    description: incident?.description ?? '',
    data_affected: incident?.data_affected ?? '',
    consequences: incident?.consequences ?? '',
    measures: incident?.measures ?? '',
    client_steps: incident?.client_steps ?? '',
    contact: incident?.contact ?? '',
    all_clients: incident?.all_clients ?? false,
    occurred: toLocal(incident?.occurred_at ?? null),
    detected: toLocal(incident?.detected_at ?? new Date().toISOString()),
    certIn: toLocal(incident?.cert_in_reported_at ?? null),
    certInReference: incident?.cert_in_reference ?? '',
    clients: incident && !incident.all_clients ? incident.clients.map((c) => ({ public_id: c.public_id, name: c.name })) : [],
  });
  const [asking, setAsking] = useState<'notify' | 'close' | null>(null);

  const saved = async (data: IncidentDetail) => {
    queryClient.setQueryData(keys.one(data.public_id), data);
    await queryClient.invalidateQueries({ queryKey: keys.list });
  };
  const body = (): IncidentBody => ({
    title: form.title,
    description: form.description,
    data_affected: form.data_affected,
    consequences: form.consequences,
    measures: form.measures,
    client_steps: form.client_steps,
    contact: form.contact,
    occurred_at: toInstant(form.occurred),
    detected_at: toInstant(form.detected) ?? undefined,
    cert_in_reported_at: toInstant(form.certIn),
    cert_in_reference: form.certInReference || null,
    all_clients: form.all_clients,
    clients: form.clients.map((c) => c.public_id),
  });
  const save = useMutation({
    mutationFn: () =>
      incident ? api.patch<IncidentDetail>(`${BASE}/${incident.public_id}`, body()) : api.post<IncidentDetail>(BASE, body()),
    onSuccess: async (data) => {
      toast.success('Saved');
      await saved(data);
      if (!incident) navigate(`/legal/incidents/${data.public_id}`, { replace: true });
    },
    onError: (error) => toast.error('Not saved', errorText(error)),
  });
  const act = useMutation({
    mutationFn: (what: 'notify' | 'close') => api.post<IncidentDetail>(`${BASE}/${incident?.public_id}/${what}`, {}),
    onSuccess: async (data, what) => {
      setAsking(null);
      toast.success(what === 'notify' ? 'Notices sent' : 'Closed');
      await saved(data);
    },
    onError: (error) => toast.error('Not done', errorText(error)),
  });
  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [key]: value }));
  const editable = canEdit && !closed;
  const told = incident?.clients.filter((c) => c.notice?.status === 'sent').length ?? 0;

  const record = (
    <div className="space-y-4">
      <Card>
        <CardHeader title="The incident" description="The clock runs from when Connect became aware of it." />
        <CardBody className="space-y-3">
          <Input label="Title" value={form.title} disabled={!editable} onChange={(e) => set('title', e.target.value)} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="When it happened" type="datetime-local" value={form.occurred} disabled={!editable} onChange={(e) => set('occurred', e.target.value)} />
            <Input label="When Connect became aware" type="datetime-local" value={form.detected} disabled={!editable} onChange={(e) => set('detected', e.target.value)} />
          </div>
          {FIELDS.map((field) => (
            <Textarea
              key={field.key}
              label={field.label}
              hint={field.hint}
              rows={field.rows}
              value={String(form[field.key as keyof typeof form] ?? '')}
              disabled={!editable}
              onChange={(e) => set(field.key as keyof typeof form, e.target.value as never)}
            />
          ))}
          <Input label="Who answers questions about it" hint="A name and an address." value={form.contact} disabled={!editable} onChange={(e) => set('contact', e.target.value)} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="CERT-In" description="Connect’s own report, due within six hours of becoming aware." />
        <CardBody className="grid gap-3 sm:grid-cols-2">
          <Input label="Reported at" type="datetime-local" value={form.certIn} disabled={!editable} onChange={(e) => set('certIn', e.target.value)} />
          <Input label="Their reference" value={form.certInReference} disabled={!editable} onChange={(e) => set('certInReference', e.target.value)} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader title="Clients affected" />
        <CardBody className="space-y-3">
          <Switch label="Every client" checked={form.all_clients} disabled={!editable} onChange={(e) => set('all_clients', e.target.checked)} />
          {!form.all_clients ? (
            <div className="space-y-2">
              <ul className="flex flex-wrap gap-2">
                {form.clients.map((client) => (
                  <li key={client.public_id}>
                    <Badge tone="neutral">
                      {client.name}
                      {editable ? (
                        <button
                          type="button"
                          aria-label={`Remove ${client.name}`}
                          className="ml-1"
                          onClick={() => set('clients', form.clients.filter((c) => c.public_id !== client.public_id))}
                        >
                          <X className="size-3" />
                        </button>
                      ) : null}
                    </Badge>
                  </li>
                ))}
              </ul>
              {editable ? (
                <RecordSelect
                  endpoint="/crm/clients"
                  value={null}
                  onChange={(id, option) => {
                    if (!id || form.clients.some((c) => c.public_id === id)) return;
                    set('clients', [...form.clients, { public_id: id, name: option?.label ?? 'A client' }]);
                  }}
                  toOption={(row) => ({ value: String(row.public_id), label: String(row.name) })}
                  placeholder="Add a client…"
                />
              ) : null}
            </div>
          ) : null}
        </CardBody>
      </Card>
      {editable ? (
        <div className="flex justify-end">
          <Button onClick={() => save.mutate()} disabled={save.isPending || !form.title.trim()}>
            {incident ? 'Save' : 'Record it'}
          </Button>
        </div>
      ) : null}
    </div>
  );

  const notices = incident ? (
    <Card>
      <CardHeader title="Notices" description="Sent once to each affected client; sending again reaches only those not yet told." />
      <CardBody>
        <ul className="divide-y divide-line text-sm">
          {incident.clients.map((client) => (
            <li key={client.public_id} className="flex flex-wrap items-center gap-2 py-2">
              <span className="min-w-0 flex-1 font-medium text-heading">{client.name}</span>
              {client.notice?.last_error ? <span className="text-xs text-danger">{client.notice.last_error}</span> : null}
              {client.notice?.sent_at ? <span className="text-xs text-muted">{formatDateTime(client.notice.sent_at)}</span> : null}
              <Badge tone={client.notice?.status === 'sent' ? 'success' : client.notice?.status === 'failed' ? 'danger' : 'warning'}>
                {client.notice?.status === 'sent' ? 'Told' : client.notice?.status === 'failed' ? 'Not delivered' : 'Not told yet'}
              </Badge>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  ) : null;

  return (
    <div className="flex flex-1 flex-col gap-4">
      <PageHeader
        title={incident ? `${incident.code} · ${incident.title}` : 'Record an incident'}
        breadcrumbs={CRUMBS}
        actions={
          incident && canPublish && !closed ? (
            <div className="flex gap-2">
              <Button onClick={() => setAsking('notify')}>
                <Send className="size-4" />
                Tell the clients
              </Button>
              <Button variant="secondary" onClick={() => setAsking('close')}>
                Close
              </Button>
            </div>
          ) : null
        }
      />
      {incident && incident.status !== 'closed' ? <Clocks incident={incident} /> : null}
      {incident ? (
        <Sections
          label="Incident"
          items={[
            { id: 'record', label: 'Record', content: record },
            {
              id: 'notices',
              label: 'Notices',
              badge: <span className="text-2xs text-faint">{told}/{incident.clients.length}</span>,
              content: notices,
            },
          ]}
        />
      ) : (
        record
      )}
      {asking ? (
        <Confirm
          open
          title={asking === 'notify' ? 'Tell the affected clients?' : 'Close this incident?'}
          confirmLabel={asking === 'notify' ? 'Send the notices' : 'Close it'}
          onConfirm={() => act.mutateAsync(asking).then(() => undefined)}
          onClose={() => setAsking(null)}
          message={
            asking === 'notify'
              ? 'Each affected client not yet told gets the notice by email now, with what is written here. Save any changes first.'
              : 'A closed incident cannot be changed. Every client must have been told.'
          }
        />
      ) : null}
    </div>
  );
}
